import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;

function EmvVerifiedSection({ userId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState([]);
  const [formData, setFormData] = useState({
    user_id: userId,
    ageGroup: '', // New state to store the age group selection
    aadhar: '',
    pan: '',
    bank_account: '',
    ifsc: '',
    aadhar_file: null,
    pan_file: null,
    bank_account_file: null,
    interview_slots: [],
    terms_accepted: false,
    monetize: false,
  });

  const [isAbove13, setIsAbove13] = useState(false);
  const [isAbove18, setIsAbove18] = useState(false);
  const [showAadhar, setShowAadhar] = useState(false);
  const [showMonetizeOptions, setShowMonetizeOptions] = useState(false);
  const [isEligible, setIsEligible] = useState(true); // To track eligibility for verification

  const validateAadhar = (aadhar) => /^[2-9]{1}[0-9]{11}$/.test(aadhar);
  const validatePan = (pan) => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
  const validateBankAccount = (bankAccount) => /^\d{10,18}$/.test(bankAccount);
  const validateIfsc = (ifsc) => /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(ifsc);

  const handleChange = (event) => {
    const { name, value, type, checked, files } = event.target;

    if (type === 'file') {
      const file = files[0];
      const fileSizeInKB = file.size / 1024; // Convert file size to KB
      if (fileSizeInKB > 500) {
        setError(['File size should not exceed 500 KB']);
        event.target.value = ''; // Clear the file input
        return;
      }
      setFormData({ ...formData, [name]: file });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }

    setError([]);
  };

  const handleAgeGroupChange = (event) => {
    const selectedAgeGroup = event.target.value;
    setFormData({ ...formData, ageGroup: selectedAgeGroup });

    if (selectedAgeGroup === 'above13') {
      setIsAbove13(true);
      setIsEligible(true);
      setShowAadhar(true); // Show Aadhar field if above 13
      setIsAbove18(false);
      setShowMonetizeOptions(false); // Hide monetization option
    } else if (selectedAgeGroup === 'above18') {
      setIsAbove13(true);
      setIsAbove18(true);
      setIsEligible(true);
      setShowAadhar(true); // Show Aadhar field
      setShowMonetizeOptions(true); // Show monetization option
    } else {
      setIsEligible(false); // User is below 13 and not eligible for verification
      setIsAbove13(false);
      setIsAbove18(false);
      setShowAadhar(false);
      setShowMonetizeOptions(false);
    }
  };

  const handleMonetizeChange = (event) => {
    const { checked } = event.target;
    setFormData({ ...formData, monetize: checked });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = [];
    if (formData.ageGroup === '') errors.push('Please select your age group.');
    if (showAadhar && !validateAadhar(formData.aadhar)) errors.push('Invalid Aadhaar number.');
    if (showMonetizeOptions && formData.monetize) {
      if (!validatePan(formData.pan)) errors.push('Invalid PAN number.');
      if (!validateBankAccount(formData.bank_account)) errors.push('Invalid Bank Account number.');
      if (!validateIfsc(formData.ifsc)) errors.push('Invalid IFSC code.');
    }

    if (errors.length > 0) {
      setError(errors); // Display validation errors
      return;
    }

    setLoading(true);

    const formDataToSubmit = new FormData();
    for (const key in formData) {
      if (formData[key] instanceof File) {
        formDataToSubmit.append(key, formData[key]);
      } else if (Array.isArray(formData[key])) {
        formDataToSubmit.append('interview_slots', JSON.stringify(formData[key]));
      } else {
        formDataToSubmit.append(key, formData[key]);
      }
    }

    try {
      const response = await axios.post(`${API_URL}/create_user_verification/?user_id=${userId}`, formDataToSubmit, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        showAlert('Verification application submitted successfully!');
        window.location.reload();
      } else {
        alert('Verification failed. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again later.');
    }

    setLoading(false);
  };

  const showAlert = (message) => {
    const alertDiv = document.createElement('div');
    alertDiv.textContent = message;
    alertDiv.style.position = 'fixed';
    alertDiv.style.top = '10px';
    alertDiv.style.left = '50%';
    alertDiv.style.transform = 'translateX(-50%)';
    alertDiv.style.padding = '10px 20px';
    alertDiv.style.backgroundColor = 'green';
    alertDiv.style.color = 'white';
    alertDiv.style.borderRadius = '5px';
    alertDiv.style.zIndex = '9999';
    alertDiv.style.fontSize = '16px';
    document.body.appendChild(alertDiv);

    setTimeout(() => alertDiv.remove(), 3000);
  };

  return (
    <div>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 'bold',
            color: 'white',
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2rem', lg: '2rem' },
          }}
        >
          Stage 2: KYC Verification
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            maxWidth: '900px',
            width: '100%',
            margin: '0 auto',
            padding: { xs: 2, sm: 3 },
          }}
        >
          <FormControl component="fieldset" required>
            <FormLabel component="legend" sx={{ color: 'white' }}>
              Please select your age
            </FormLabel>
            <RadioGroup
              row
              name="ageGroup"
              value={formData.ageGroup}
              onChange={handleAgeGroupChange}
              sx={{
                '& .MuiRadio-root': {
                  color: '#1E90FF', // Turn radio button blue on click
                  '&.Mui-checked': {
                    color: '#1E90FF', // Blue when checked
                  },
                },
              }}
            >
              <FormControlLabel value="above13" control={<Radio />} label="above 13" />
              <FormControlLabel value="below13" control={<Radio />} label="below 13" />
              <FormControlLabel value="above18" control={<Radio />} label="above 18" />
            </RadioGroup>
            {!isEligible && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                You are not eligible for verification as you are under 13 years old.
              </Typography>
            )}
          </FormControl>

          {showAadhar && (
            <>
              <TextField
                fullWidth
                label="Aadhar Number"
                name="aadhar"
                value={formData.aadhar}
                onChange={handleChange}
                margin="normal"
                required
                InputLabelProps={{ style: { color: '#ffffff' } }}
                InputProps={{ style: { color: '#ffffff' } }}
              />
              {error && error.some((e) => e.includes('Aadhaar')) && (
                <Typography color="error" variant="body2">
                  {error.find((e) => e.includes('Aadhaar'))}
                </Typography>
              )}
              <FormHelperText sx={{ color: 'text.secondary' }}>
                Enter your Aadhar Number. It will be used for identity verification.
              </FormHelperText>
              <input
                type="file"
                name="aadhar_file"
                onChange={handleChange}
                accept="application/pdf,image/*"
                required
              />
            </>
          )}

          {showMonetizeOptions && (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.monetize}
                    onChange={handleMonetizeChange}
                    name="monetize"
                    sx={{
                      color: '#1E90FF',
                      '&.Mui-checked': { color: '#1E90FF' },
                    }}
                  />
                }
                label="I want to monetize/earn from the platform"
              />
              {formData.monetize && (
                <>
                  <TextField
                    fullWidth
                    label="PAN Number"
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    margin="normal"
                    required
                    InputLabelProps={{ style: { color: '#ffffff' } }}
                    InputProps={{ style: { color: '#ffffff' } }}
                  />
                  {error && error.some((e) => e.includes('PAN')) && (
                    <Typography color="error" variant="body2">
                      {error.find((e) => e.includes('PAN'))}
                    </Typography>
                  )}
                  <FormHelperText sx={{ color: 'text.secondary' }}>
                    Upload your PAN card copy. Max file size: 500KB.
                  </FormHelperText>
                  <input
                    type="file"
                    name="pan_file"
                    onChange={handleChange}
                    accept="application/pdf,image/*"
                    required
                  />

                  <TextField
                    fullWidth
                    label="Bank Account Number"
                    name="bank_account"
                    value={formData.bank_account}
                    onChange={handleChange}
                    margin="normal"
                    required
                    InputLabelProps={{ style: { color: '#ffffff' } }}
                    InputProps={{ style: { color: '#ffffff' } }}
                  />
                  {error && error.some((e) => e.includes('Bank Account')) && (
                    <Typography color="error" variant="body2">
                      {error.find((e) => e.includes('Bank Account'))}
                    </Typography>
                  )}
                  <FormHelperText sx={{ color: 'text.secondary' }}>
                    Upload your Bank Passbook copy. Max file size: 500KB.
                  </FormHelperText>
                  <input
                    type="file"
                    name="bank_account_file"
                    onChange={handleChange}
                    accept="application/pdf,image/*"
                    required
                  />

                  <TextField
                    fullWidth
                    label="IFSC Code"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleChange}
                    margin="normal"
                    required
                    InputLabelProps={{ style: { color: '#ffffff' } }}
                    InputProps={{ style: { color: '#ffffff' } }}
                  />
                  {error && error.some((e) => e.includes('IFSC')) && (
                    <Typography color="error" variant="body2">
                      {error.find((e) => e.includes('IFSC'))}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.terms_accepted}
                onChange={handleChange}
                name="terms_accepted"
                sx={{
                  color: '#ffffff',
                  '&.Mui-checked': { color: '#ffffff' },
                  '& .MuiSvgIcon-root': { borderColor: 'white' },
                }}
              />
            }
            label={
              <span style={{ color: '#ffffff' }}>
                I agree to the{' '}
                <Link to="/terms-of-service" style={{ textDecoration: 'none', color: '#1E90FF' }}>
                  Terms of Service
                </Link>
                ,{' '}
                <Link to="/privacy-policy" style={{ textDecoration: 'none', color: '#1E90FF' }}>
                  Privacy Policy
                </Link>
                , and{' '}
                <Link to="/payments-policy" style={{ textDecoration: 'none', color: '#1E90FF' }}>
                  Payment Policies
                </Link>
              </span>
            }
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading || !formData.terms_accepted || !isEligible}
            sx={{
              marginTop: 2,
              backgroundColor: '#90caf9',
              '&:hover': { backgroundColor: '#64b5f6' },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </Box>
      </form>
    </div>
  );
}

export default EmvVerifiedSection;
